import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Bell, Search, Plus, X, Menu, Building2, HelpCircle } from 'lucide-react';
import { HELP_BY_PATH } from '../../data/helpContent';
import { useAuth } from '../../context/AuthContext';
import WaffleMenu from './WaffleMenu';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Header({ onMenuToggle }) {
    const { user, getAuthHeader } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [notifications, setNotifications] = useState([]);
    const [showNotifs, setShowNotifs] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showMobileSearch, setShowMobileSearch] = useState(false);

    useEffect(() => {
        const fetchNotifs = async () => {
            try {
                const res = await axios.get(`${API}/notifications`, { headers: getAuthHeader() });
                setNotifications(Array.isArray(res.data) ? res.data : []);
            } catch {}
        };
        fetchNotifs();
    }, []);

    const unreadCount = Array.isArray(notifications) ? notifications.filter(n => !n.is_read).length : 0;

    const markAllRead = async () => {
        try {
            await axios.put(`${API}/notifications/read-all`, {}, { headers: getAuthHeader() });
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        } catch {}
    };

    const getPageTitle = () => {
        const path = location.pathname;
        if (path === '/dashboard') return 'Dashboard';
        if (path.startsWith('/accounts')) return 'Companies';
        if (path.startsWith('/contacts')) return 'Contacts';
        if (path.startsWith('/pipeline')) return 'Pipeline';
        if (path.startsWith('/deal-registration')) return 'Deal Registration';
        if (path.startsWith('/proposals')) return 'Proposals';
        if (path.startsWith('/ai-assistant')) return 'AI Assistant';
        if (path.startsWith('/ai-heartbeat')) return 'AI Heartbeat';
        if (path.startsWith('/communications')) return 'Communications';
        if (path.startsWith('/activities')) return 'Activities';
        if (path.startsWith('/reports')) return 'Reports';
        if (path.startsWith('/settings')) return 'Settings';
        if (path.startsWith('/inbox')) return 'Inbox';
        if (path.startsWith('/booking')) return 'Booking Links';
        if (path.startsWith('/super-admin')) return 'Platform Admin';
        if (path.startsWith('/admin')) return 'Admin';
        if (path.startsWith('/sites')) return 'Sites';
        if (path.startsWith('/list-management')) return 'Marketing';
        if (path.startsWith('/campaigns')) return 'Marketing';
        if (path.startsWith('/events')) return 'Marketing';
        if (path.startsWith('/forms')) return 'Marketing';
        if (path.startsWith('/content')) return 'Marketing';
        if (path.startsWith('/testimonials')) return 'Marketing';
        if (path.startsWith('/affiliates')) return 'Marketing';
        return 'CRM';
    };

    const handleSearch = (e) => {
        if (e.key === 'Enter' && searchQuery) {
            navigate(`/contacts?search=${searchQuery}`);
            setShowMobileSearch(false);
        }
    };

    return (
        <header className="h-14 sm:h-16 border-b border-stone-200 flex items-center justify-between px-3 sm:px-6 sticky top-0 z-10" style={{ backgroundColor: 'rgba(250,250,245,0.85)', backdropFilter: 'blur(12px)' }}>
            <div className="flex items-center gap-3">
                {/* Mobile hamburger */}
                <button
                    onClick={onMenuToggle}
                    className="p-2 rounded-lg hover:bg-stone-100 text-stone-600 lg:hidden"
                    data-testid="mobile-menu-btn"
                >
                    <Menu className="w-5 h-5" />
                </button>
                <h1 className="text-lg sm:text-xl font-semibold text-stone-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {getPageTitle()}
                </h1>
            </div>

            <div className="flex items-center gap-1.5 sm:gap-3">
                {/* Org info */}
                {user?.org_name && (
                    <div className="hidden md:flex items-center gap-1.5 px-3 py-1 bg-stone-100 rounded-lg mr-1">
                        <Building2 className="w-3.5 h-3.5 text-stone-400" />
                        <span className="text-xs font-medium text-stone-600">{user.org_name}</span>
                        {user?.org_id && (
                            <span className="text-[10px] text-stone-400 font-mono">{user.org_id.slice(0, 8)}</span>
                        )}
                    </div>
                )}

                {/* Desktop Search */}
                <div className="relative hidden sm:block">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <input
                        type="text"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        onKeyDown={handleSearch}
                        data-testid="global-search-input"
                        className="pl-9 pr-4 py-2 text-sm bg-white border border-stone-200 rounded-lg w-40 lg:w-52 focus:outline-none focus:ring-2 focus:ring-stone-900/10 focus:border-stone-400 transition-all"
                    />
                </div>

                {/* Mobile Search Toggle */}
                <button
                    onClick={() => setShowMobileSearch(!showMobileSearch)}
                    className="p-2 rounded-lg hover:bg-stone-100 text-stone-500 sm:hidden"
                    data-testid="mobile-search-btn"
                >
                    <Search className="w-5 h-5" />
                </button>

                {/* Help Button */}
                <button
                    onClick={() => {
                        const path = location.pathname;
                        // Try exact match first, then strip ID segments for detail pages
                        const slug = HELP_BY_PATH[path] || HELP_BY_PATH['/' + path.split('/')[1]] || '';
                        navigate(slug ? `/help#${slug}` : '/help');
                    }}
                    className="p-2 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-600 transition-colors"
                    title="Help Guide"
                >
                    <HelpCircle className="w-5 h-5" />
                </button>

                {/* Quick Add */}
                <button
                    onClick={() => navigate('/deals/new')}
                    data-testid="quick-add-btn"
                    className="flex items-center gap-2 px-2.5 sm:px-3 py-2 bg-stone-900 text-white text-sm font-medium rounded-lg hover:bg-stone-800 transition-colors shadow-sm"
                >
                    <Plus className="w-4 h-4" />
                    <span className="hidden md:block">New Deal</span>
                </button>

                {/* Notifications */}
                <div className="relative">
                    <button
                        onClick={() => setShowNotifs(!showNotifs)}
                        data-testid="notifications-btn"
                        className="relative p-2 rounded-lg hover:bg-stone-100 text-stone-500 hover:text-stone-700 transition-colors"
                    >
                        <Bell className="w-5 h-5" />
                        {unreadCount > 0 && (
                            <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                                {unreadCount}
                            </span>
                        )}
                    </button>

                    {showNotifs && (
                        <div className="absolute right-0 top-12 w-72 sm:w-80 bg-white rounded-xl border border-stone-200 shadow-xl z-50 animate-fade-in">
                            <div className="flex items-center justify-between px-4 py-3 border-b border-stone-200">
                                <h3 className="font-semibold text-stone-900 text-sm">Notifications</h3>
                                <div className="flex items-center gap-2">
                                    {unreadCount > 0 && (
                                        <button onClick={markAllRead} className="text-xs text-stone-600 hover:text-stone-900 font-medium">Mark all read</button>
                                    )}
                                    <button onClick={() => setShowNotifs(false)} className="p-1 rounded hover:bg-stone-100"><X className="w-3 h-3" /></button>
                                </div>
                            </div>
                            <div className="max-h-72 overflow-y-auto">
                                {notifications.length === 0 ? (
                                    <div className="p-6 text-center text-stone-500 text-sm">No notifications</div>
                                ) : (
                                    notifications.slice(0, 8).map(n => (
                                        <div key={n.notification_id} className={`px-4 py-3 border-b border-stone-100 hover:bg-stone-50 cursor-pointer ${!n.is_read ? 'bg-amber-50/50' : ''}`}>
                                            <p className="text-sm font-medium text-stone-900">{n.title}</p>
                                            <p className="text-xs text-stone-500 mt-0.5">{n.message}</p>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* App Launcher */}
                <WaffleMenu appLinks={user?.app_links || []} />

                {/* User Avatar */}
                <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/settings')}>
                    <div className="w-8 h-8 rounded-full overflow-hidden bg-stone-200">
                        {user?.picture ? (
                            <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-stone-700 font-semibold text-xs">
                                {user?.name?.charAt(0)}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Mobile Search Overlay */}
            {showMobileSearch && (
                <div className="absolute top-14 left-0 right-0 p-3 bg-white border-b border-stone-200 shadow-lg sm:hidden z-20">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                        <input
                            type="text"
                            placeholder="Search contacts..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            onKeyDown={handleSearch}
                            autoFocus
                            className="w-full pl-9 pr-10 py-2.5 text-sm bg-stone-50 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10"
                        />
                        <button
                            onClick={() => setShowMobileSearch(false)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-stone-200"
                        >
                            <X className="w-4 h-4 text-stone-500" />
                        </button>
                    </div>
                </div>
            )}
        </header>
    );
}
